/**
 * ChatMediaInput — image picker + preview strip for DM chat.
 * Max 3 images, sequential upload to R2 via existing pipeline.
 * Shows upload progress, remove button per image.
 */

import React from 'react'
import { X, ImagePlus, Loader2 } from 'lucide-react' // Loader2 used in ChatMediaPreview
import { motion, AnimatePresence } from 'framer-motion'

// ── Constants ──────────────────────────────────────────────

const MAX_IMAGES = 3

// ── Types ──────────────────────────────────────────────────

interface ChatMediaInputProps {
  images: string[]
  isUploading: boolean
  onPickFiles: () => void
  disabled?: boolean
}

// ── Component ──────────────────────────────────────────────

export const ChatMediaInput: React.FC<ChatMediaInputProps> = ({
  images,
  isUploading,
  onPickFiles,
  disabled = false,
}) => {
  const canAddMore = images.length < MAX_IMAGES && !isUploading

  return (
    <div className="flex items-center gap-1.5">
      {/* Pick button */}
      <button
        type="button"
        onClick={onPickFiles}
        disabled={disabled || !canAddMore}
        className="w-9 h-9 flex items-center justify-center rounded-full shrink-0
          transition-colors tap-highlight-transparent
          hover:bg-white/[0.06] active:bg-white/[0.08]
          disabled:opacity-30 disabled:pointer-events-none"
      >
        <ImagePlus size={16} className="text-text-hint" />
      </button>
    </div>
  )
}

// ── Preview Strip (shown above input when images selected) ──

interface ChatMediaPreviewProps {
  images: string[]
  isUploading: boolean
  onRemoveImage: (index: number) => void
}

export const ChatMediaPreview: React.FC<ChatMediaPreviewProps> = ({
  images,
  isUploading,
  onRemoveImage,
}) => {
  if (images.length === 0 && !isUploading) return null

  return (
    <div className="flex gap-2 px-3 py-2 overflow-x-auto scroll-snap-x">
      <AnimatePresence mode="popLayout">
        {images.map((url, i) => (
          <motion.div
            key={url}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            className="relative shrink-0 w-16 h-16 rounded-xl overflow-hidden
              border border-white/[0.06] scroll-snap-start"
          >
            <img
              src={url}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <button
              type="button"
              onClick={() => onRemoveImage(i)}
              className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full
                bg-black/60 backdrop-blur-sm flex items-center justify-center
                hover:bg-black/80 transition-colors tap-highlight-transparent"
            >
              <X size={10} className="text-white" />
            </button>
          </motion.div>
        ))}

        {/* Upload spinner placeholder */}
        {isUploading && (
          <motion.div
            key="uploading"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="shrink-0 w-16 h-16 rounded-xl border border-white/[0.06]
              bg-white/[0.03] flex items-center justify-center"
          >
            <Loader2 size={16} className="text-text-hint animate-spin" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ── Hidden file input helper ───────────────────────────────

interface ChatFileInputProps {
  onFilesSelected: (files: FileList) => void
  inputRef: React.RefObject<HTMLInputElement | null>
}

export const ChatFileInput: React.FC<ChatFileInputProps> = ({
  onFilesSelected,
  inputRef,
}) => (
  <input
    ref={inputRef}
    type="file"
    accept="image/jpeg,image/png,image/webp,image/heic"
    multiple
    className="hidden"
    onChange={(e) => {
      if (e.target.files?.length) {
        onFilesSelected(e.target.files)
        e.target.value = ''
      }
    }}
  />
)
